import React, { useState } from 'react';
import { usePageTitle } from '../hooks/usePageTitle';
import api from '../utils/api';
import SEOHelmet from '../components/SEOHelmet';

const Contact = () => {
  usePageTitle('Contact Us');
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    setSubmitting(true);

    try {
      const response = await api.post('/api/contact', formData);
      setSuccess(true);
      setFormData({ name: '', email: '', subject: '', message: '' });
      
      // Scroll to top to show success message
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err: any) {
      const message = err.response?.data?.message || 'Failed to send message. Please try again later.';
      setError(message);
      console.error('Contact form error:', err.response?.data || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <SEOHelmet
        title="Contact Us"
        description="Get in touch with CEC Tracker. Send us your questions, feedback, or support requests and we'll get back to you as soon as possible."
        canonical={`${window.location.origin}/contact`}
      />
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-lg-8">
            <div className="card shadow-sm">
              <div className="card-body p-4 p-md-5">
                <h1 className="text-center mb-4">Contact Us</h1>
                <p className="text-center text-muted mb-4">
                  Have a question or need help? Send us a message and we'll get back to you as soon as possible.
                </p>

                {success && (
                  <div className="alert alert-success alert-dismissible fade show" role="alert">
                    <i className="bi bi-check-circle-fill me-2"></i>
                    <strong>Success!</strong> Your message has been sent. We'll get back to you soon!
                    <button 
                      type="button" 
                      className="btn-close" 
                      onClick={() => setSuccess(false)}
                      aria-label="Close"
                    ></button>
                  </div>
                )}

                {error && (
                  <div className="alert alert-danger alert-dismissible fade show" role="alert">
                    <i className="bi bi-exclamation-triangle-fill me-2"></i>
                    {error}
                    <button 
                      type="button" 
                      className="btn-close" 
                      onClick={() => setError('')}
                      aria-label="Close"
                    ></button>
                  </div>
                )}

                <form onSubmit={handleSubmit}>
                  <div className="mb-3">
                    <label htmlFor="name" className="form-label">
                      Name <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      className="form-control"
                      placeholder="Your full name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      disabled={submitting}
                    />
                  </div>

                  <div className="mb-3">
                    <label htmlFor="email" className="form-label">
                      Email <span className="text-danger">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      className="form-control"
                      placeholder="your.email@example.com"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      disabled={submitting}
                    />
                  </div>

                  <div className="mb-3">
                    <label htmlFor="subject" className="form-label">
                      Subject <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      className="form-control"
                      placeholder="Brief description of your inquiry"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      disabled={submitting}
                    />
                  </div>

                  <div className="mb-4">
                    <label htmlFor="message" className="form-label">
                      Message <span className="text-danger">*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      className="form-control"
                      rows={6}
                      placeholder="Tell us how we can help you..."
                      value={formData.message}
                      onChange={handleChange}
                      required
                      disabled={submitting}
                    />
                  </div>

                  <div className="d-grid">
                    <button 
                      type="submit" 
                      className="btn btn-primary btn-lg"
                      disabled={submitting}
                    >
                      {submitting ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Sending...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-send-fill me-2"></i>
                          Send Message
                        </>
                      )}
                    </button>
                  </div>
                </form>

                <hr className="my-4" />

                <div className="row text-center">
                  <div className="col-md-6 mb-3 mb-md-0">
                    <i className="bi bi-envelope-fill text-primary fs-3 mb-2"></i>
                    <p className="mb-1 fw-semibold">Email</p>
                    <p className="text-muted small">support@cectracker.com</p>
                  </div>
                  <div className="col-md-6">
                    <i className="bi bi-clock-fill text-primary fs-3 mb-2"></i>
                    <p className="mb-1 fw-semibold">Response Time</p>
                    <p className="text-muted small">Usually within 24 hours</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Contact;
